/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { chatCreditUsage } from "../funcs/chatCreditUsage.js";
import { chatDelete } from "../funcs/chatDelete.js";
import { chatGenerateTitle } from "../funcs/chatGenerateTitle.js";
import { chatGetAssistantSessionSummary } from "../funcs/chatGetAssistantSessionSummary.js";
import { chatGetWorkUnitsTrend } from "../funcs/chatGetWorkUnitsTrend.js";
import { chatList } from "../funcs/chatList.js";
import { chatListSessionLinks } from "../funcs/chatListSessionLinks.js";
import { chatListSources } from "../funcs/chatListSources.js";
import { chatLoad } from "../funcs/chatLoad.js";
import { chatLoadOverview } from "../funcs/chatLoadOverview.js";
import { chatSetPinned } from "../funcs/chatSetPinned.js";
import { chatSubmitFeedback } from "../funcs/chatSubmitFeedback.js";
import { chatSummarize } from "../funcs/chatSummarize.js";
import { chatSummarizeToolCall } from "../funcs/chatSummarizeToolCall.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { AssistantSessionSummary } from "../models/components/assistantsessionsummary.js";
import { CaptureEventResult } from "../models/components/captureeventresult.js";
import { Chat as Chat$Model } from "../models/components/chat.js";
import { ChatOverview } from "../models/components/chatoverview.js";
import { CreditUsageResponseBody } from "../models/components/creditusageresponsebody.js";
import { GenerateTitleResponseBody } from "../models/components/generatetitleresponsebody.js";
import { ListChatsResult } from "../models/components/listchatsresult.js";
import { ListSessionLinksResult } from "../models/components/listsessionlinksresult.js";
import { ListSourcesResult } from "../models/components/listsourcesresult.js";
import { SummarizeChatResult } from "../models/components/summarizechatresult.js";
import { SummarizeToolCallResult } from "../models/components/summarizetoolcallresult.js";
import { WorkUnitsTrendResult } from "../models/components/workunitstrendresult.js";
import {
  CreditUsageRequest,
  CreditUsageSecurity,
} from "../models/operations/creditusage.js";
import {
  DeleteChatRequest,
  DeleteChatSecurity,
} from "../models/operations/deletechat.js";
import {
  GenerateTitleRequest,
  GenerateTitleSecurity,
} from "../models/operations/generatetitle.js";
import {
  GetAssistantSessionSummaryRequest,
  GetAssistantSessionSummarySecurity,
} from "../models/operations/getassistantsessionsummary.js";
import {
  GetWorkUnitsTrendRequest,
  GetWorkUnitsTrendSecurity,
} from "../models/operations/getworkunitstrend.js";
import {
  ListChatsRequest,
  ListChatsSecurity,
} from "../models/operations/listchats.js";
import {
  ListChatSessionLinksRequest,
  ListChatSessionLinksSecurity,
} from "../models/operations/listchatsessionlinks.js";
import {
  ListChatSourcesRequest,
  ListChatSourcesSecurity,
} from "../models/operations/listchatsources.js";
import {
  LoadChatRequest,
  LoadChatSecurity,
} from "../models/operations/loadchat.js";
import {
  LoadChatOverviewRequest,
  LoadChatOverviewSecurity,
} from "../models/operations/loadchatoverview.js";
import {
  SetChatPinnedRequest,
  SetChatPinnedSecurity,
} from "../models/operations/setchatpinned.js";
import {
  SubmitFeedbackRequest,
  SubmitFeedbackSecurity,
} from "../models/operations/submitfeedback.js";
import {
  SummarizeChatRequest,
  SummarizeChatSecurity,
} from "../models/operations/summarizechat.js";
import {
  SummarizeToolCallRequest,
  SummarizeToolCallSecurity,
} from "../models/operations/summarizetoolcall.js";
import { unwrapAsync } from "../types/fp.js";

export class Chat extends ClientSDK {
  /**
   * creditUsage chat
   *
   * @remarks
   * Get the total number of chat credits and usage for the current billing period
   */
  async creditUsage(
    request?: CreditUsageRequest | undefined,
    security?: CreditUsageSecurity | undefined,
    options?: RequestOptions,
  ): Promise<CreditUsageResponseBody> {
    return unwrapAsync(chatCreditUsage(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteChat chat
   *
   * @remarks
   * Soft-delete a chat by its ID
   */
  async delete(
    request: DeleteChatRequest,
    security?: DeleteChatSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(chatDelete(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * generateTitle chat
   *
   * @remarks
   * Read or set a chat's title. Omit `title` to return the current/auto-generated title (titles are generated asynchronously after a completion). Provide `title` to set a manual title that auto-generation will never overwrite; provide an empty `title` to clear the manual title and re-enable auto-generation.
   */
  async generateTitle(
    request: GenerateTitleRequest,
    security?: GenerateTitleSecurity | undefined,
    options?: RequestOptions,
  ): Promise<GenerateTitleResponseBody> {
    return unwrapAsync(chatGenerateTitle(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getAssistantSessionSummary chat
   *
   * @remarks
   * Get assistant session activity totals for a time range.
   */
  async getAssistantSessionSummary(
    request: GetAssistantSessionSummaryRequest,
    security?: GetAssistantSessionSummarySecurity | undefined,
    options?: RequestOptions,
  ): Promise<AssistantSessionSummary> {
    return unwrapAsync(chatGetAssistantSessionSummary(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getWorkUnitsTrend chat
   *
   * @remarks
   * Aggregate work-units analysis results over time for the project: work done and cost/token efficiency per UTC day.
   */
  async getWorkUnitsTrend(
    request?: GetWorkUnitsTrendRequest | undefined,
    security?: GetWorkUnitsTrendSecurity | undefined,
    options?: RequestOptions,
  ): Promise<WorkUnitsTrendResult> {
    return unwrapAsync(chatGetWorkUnitsTrend(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listChats chat
   *
   * @remarks
   * List all chats for a project
   */
  async list(
    request?: ListChatsRequest | undefined,
    security?: ListChatsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListChatsResult> {
    return unwrapAsync(chatList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listSessionLinks chat
   *
   * @remarks
   * List session-lineage links touching the given chats (session portability). A link records that a session was moved to another harness; the child side is present when the continuation's session id was known at move time. Returns every link where a requested chat is either the parent or the child, honoring the same visibility scoping as listChats.
   */
  async listSessionLinks(
    request: ListChatSessionLinksRequest,
    security?: ListChatSessionLinksSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListSessionLinksResult> {
    return unwrapAsync(chatListSessionLinks(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listSources chat
   *
   * @remarks
   * List the distinct agent sources present in this project's chats, for populating the agent-type filter on the Agent Sessions page.
   */
  async listSources(
    request?: ListChatSourcesRequest | undefined,
    security?: ListChatSourcesSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListSourcesResult> {
    return unwrapAsync(chatListSources(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * loadChat chat
   *
   * @remarks
   * Load a chat by its ID. Messages within a generation are paginated by `seq` keyset: omit cursors to receive the newest page, pass `before_seq` to load older messages (scroll up) or `after_seq` to load newer ones (scroll down). Set `from_start` to receive the oldest page (the start of the thread) instead of the newest. Omit `generation` to receive the latest generation. Set `risk_only` to return only messages with risk findings plus a few messages of surrounding context per finding. Set `query` to instead return only messages whose text matches a search query plus surrounding context (mutually exclusive with `risk_only`).
   */
  async load(
    request: LoadChatRequest,
    security?: LoadChatSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Chat$Model> {
    return unwrapAsync(chatLoad(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * loadChatOverview chat
   *
   * @remarks
   * Load authorized chat overview metadata by exact ID without reading messages or recording a transcript-open audit event.
   */
  async loadOverview(
    request: LoadChatOverviewRequest,
    security?: LoadChatOverviewSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ChatOverview> {
    return unwrapAsync(chatLoadOverview(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setPinned chat
   *
   * @remarks
   * Pin or unpin a chat. Pinned chats surface in a dedicated section above recents on the chat page.
   */
  async setPinned(
    request: SetChatPinnedRequest,
    security?: SetChatPinnedSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(chatSetPinned(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * submitFeedback chat
   *
   * @remarks
   * Submit user feedback for a chat (success/failure)
   */
  async submitFeedback(
    request: SubmitFeedbackRequest,
    security?: SubmitFeedbackSecurity | undefined,
    options?: RequestOptions,
  ): Promise<CaptureEventResult> {
    return unwrapAsync(chatSubmitFeedback(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * summarize chat
   *
   * @remarks
   * Generate or return a persisted LLM summary of a chat session transcript. When a summary already exists and regenerate is false, returns the cached summary without calling the model.
   */
  async summarize(
    request: SummarizeChatRequest,
    security?: SummarizeChatSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SummarizeChatResult> {
    return unwrapAsync(chatSummarize(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * summarizeToolCall chat
   *
   * @remarks
   * Generate or return a persisted two-sentence summary of one tool call. Concurrent requests share the same cached result.
   */
  async summarizeToolCall(
    request: SummarizeToolCallRequest,
    security?: SummarizeToolCallSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SummarizeToolCallResult> {
    return unwrapAsync(chatSummarizeToolCall(
      this,
      request,
      security,
      options,
    ));
  }
}
